/**
 * React Query hooks for the admin transcription-workflow registry and per-workspace setting (T-210).
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  adminListWorkflows,
  adminGetWorkspaceWorkflow,
  adminSetWorkspaceWorkflow,
  type WorkflowEntry,
  type WorkspaceWorkflow,
} from "../services/naraDoor";

export function useWorkflows() {
  return useQuery<{ workflows: WorkflowEntry[] }>({
    queryKey: ["adminWorkflows"],
    queryFn: () =>
      adminListWorkflows().catch((error: Error) => {
        console.error("adminListWorkflows failed", { error: error.message });
        throw error;
      }),
  });
}

export function useWorkspaceWorkflow(workspaceId: string | undefined) {
  return useQuery<WorkspaceWorkflow>({
    queryKey: ["adminWorkspaceWorkflow", workspaceId],
    queryFn: () => {
      if (!workspaceId) throw new Error("workspaceId is required");
      return adminGetWorkspaceWorkflow(workspaceId).catch((error: Error) => {
        console.error("adminGetWorkspaceWorkflow failed", { workspaceId, error: error.message });
        throw error;
      });
    },
    enabled: !!workspaceId,
  });
}

export function useSetWorkspaceWorkflow(workspaceId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<WorkspaceWorkflow, Error, string>({
    mutationFn: (workflow) => {
      if (!workspaceId) throw new Error("workspaceId is required");
      return adminSetWorkspaceWorkflow(workspaceId, workflow);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminWorkspaceWorkflow", workspaceId] });
    },
    onError: (error) => {
      console.error("adminSetWorkspaceWorkflow failed", { workspaceId, error: error.message });
    },
  });
}
